import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactFlowInstance } from '@xyflow/react';
import type { FlowNode } from '../types';
import { Icon } from './Icon';

interface Props {
  open: boolean;
  nodes: FlowNode[];
  onClose: () => void;
  onSelect: (id: string) => void;
  rfInstance: ReactFlowInstance | null;
}

function kindLabel(kind: string): string {
  switch (kind) {
    case 'trigger': return 'When';
    case 'wait':    return 'Wait';
    case 'condition': return 'If';
    case 'action':  return 'Then';
    case 'ai_smart': return 'AI';
    default: return kind;
  }
}

function kindVariant(kind: string): 'blue' | 'amber' | 'green' | 'muted' | 'gold' | 'silver' {
  switch (kind) {
    case 'trigger': return 'blue';
    case 'condition': return 'amber';
    case 'action': return 'green';
    case 'wait': return 'muted';
    case 'ai_smart': return 'gold';
    default: return 'silver';
  }
}

export function NodeSearch({ open, nodes, onClose, onSelect, rfInstance }: Props) {
  const [query, setQuery] = useState('');
  const [hoverIdx, setHoverIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return nodes.slice(0, 20);
    return nodes.filter(n => {
      const d: any = n.data;
      const hay = `${d.label || ''} ${d.body || ''} ${d.field || ''} ${d.value || ''} ${d.source || ''} ${d.type || ''}`.toLowerCase();
      return hay.includes(q);
    }).slice(0, 20);
  }, [query, nodes]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setHoverIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  function selectAndFocus(node: FlowNode) {
    onSelect(node.id);
    if (rfInstance) {
      rfInstance.fitView({ nodes: [node], duration: 500, padding: 0.5, maxZoom: 1.5 });
    }
    onClose();
  }

  function handleKey(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHoverIdx(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHoverIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[hoverIdx]) {
      e.preventDefault();
      selectAndFocus(results[hoverIdx]);
    }
  }

  if (!open) return null;

  return (
    <div className="confirm-root node-search-root">
      <div className="confirm-backdrop" onClick={onClose} />
      <div className="node-search-card">
        <div className="node-search-input">
          <Icon name="search" size={16} variant="silver" />
          <input
            ref={inputRef}
            value={query}
            onChange={e => { setQuery(e.target.value); setHoverIdx(0); }}
            onKeyDown={handleKey}
            placeholder="Search by label, body, field, value…"
          />
          <button className="ns-close" onClick={onClose} aria-label="Close">
            <Icon name="x" size={12} variant="muted" />
          </button>
        </div>
        <div className="node-search-results">
          {results.length === 0 && (
            <div className="ns-empty">
              <Icon name="search" size={20} variant="muted" />
              <span>No blocks match "{query}".</span>
            </div>
          )}
          {results.map((n, i) => {
            const d: any = n.data;
            const preview = d.body || d.prompt || d.source || d.type || d.field || '';
            return (
              <div
                key={n.id}
                className={`ns-row ${i === hoverIdx ? 'on' : ''}`}
                onMouseEnter={() => setHoverIdx(i)}
                onMouseDown={() => selectAndFocus(n)}
              >
                <span className={`ns-kind ${d.kind}`}>
                  <Icon
                    name={d.kind === 'trigger' ? 'lightning' : d.kind === 'condition' ? 'diamond' : d.kind === 'action' ? 'play' : d.kind === 'ai_smart' ? 'sparkle' : 'clock'}
                    size={12}
                    variant={kindVariant(d.kind)}
                  />
                  {kindLabel(d.kind)}
                </span>
                <div className="ns-info">
                  <b>{d.label || 'Untitled'}</b>
                  {preview && <span>{String(preview).slice(0, 70)}{String(preview).length > 70 ? '…' : ''}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="node-search-foot">
          <kbd>↑↓</kbd> nav · <kbd>↵</kbd> jump · <kbd>Esc</kbd> close · <span style={{ color: 'var(--gold-light)' }}>{results.length} of {nodes.length}</span>
        </div>
      </div>
    </div>
  );
}
